import React, { useState } from 'react'

import {
  Icon,
  AIGeneratedGradientFilled,
  Loader,
  CircleCheckGradientFilled,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { shape, string, bool } from 'prop-types'

import { clearGenerationResult } from 'Pods/Shared/GenerationStatus/actions'
import useOwnedGeneration from 'Pods/UnifiedNavigation/Hooks/useOwnedGeneration'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

const ICON_SIZE = { width: '20', height: '20' }
const SUCCEEDED = 'succeeded'

// Mirrors the Create with AI menu trigger (see MenuItem): a spinner while a run is in
// flight, a check once one settles (success or error - the outcome is distinguished in
// the dropdown, not here), the gradient AI glyph when idle.
const STATUS_ICONS = {
  generating: Loader,
  enhancing: Loader,
  succeeded: CircleCheckGradientFilled,
  failed: CircleCheckGradientFilled,
}
// Only the spinner needs a modifier: it drives the rotate animation and teal stroke.
// The gradient glyphs bake their own fill, so the other states need none.
const STATUS_ICON_MODIFIERS = {
  generating: 'spinner',
  enhancing: 'spinner',
}
// Existing server-translated generation labels, reused as the accessible name so this
// button needs no locale keys of its own. Idle falls back to the trigger's own title.
const STATUS_LABEL_KEYS = {
  generating: 'generatingTitle',
  enhancing: 'generatingTitle',
  succeeded: 'readyTitle',
  failed: 'errorTitle',
}
// HeaderNavigationTracker only emits header_navigation when the clicked node carries both
// data-track-label and data-track-text (see UserTracker#handleLinkClick), so the state has
// to resolve to an English, locale-independent text. Same vocabulary as the dropdown's
// result row so the two entry points aggregate together.
const STATUS_TRACK_TEXTS = {
  generating: 'Generation in progress',
  enhancing: 'Generation in progress',
  succeeded: 'Generation ready',
  failed: 'Generation issue',
}
const IDLE_TRACK_TEXT = 'Create with AI'

// Generation state in the actions bar, mobile only (< 960px - CSS hides it from md up).
// From md the Create with AI menu trigger carries this glyph inline, but below that the
// trigger is folded into the hamburger, so the state would be invisible until the user
// opens the menu. This hoists it next to cart / inbox / notifications.
//
// It doubles as the mobile Create with AI entry point: a settled successful run links
// straight to the generated model (and clears the result, like the dropdown's result row
// and the toast's Open Model CTA), every other state links to the studio.
function GenerationStatusAction({ createAiItem = EMPTY_OBJECT, labels = EMPTY_OBJECT, currentUserId = null }) {
  const { status, lastResult, activeRunCount } = useOwnedGeneration(currentUserId)
  const [pinnedHref, setPinnedHref] = useState(null)
  const IconComponent = STATUS_ICONS[status] || AIGeneratedGradientFilled
  const iconModifier = STATUS_ICON_MODIFIERS[status]
  // Figma: the number of in-flight generations sits inside the spinner ring. It is
  // decorative - aria-label / track text already announce the in-flight state - and
  // ships behind the :enable_multiple_generations flag (delivered with the labels).
  const showCount = iconModifier === 'spinner'
    && activeRunCount > 0
    && labels.multipleGenerationsEnabled === true
  const label = labels[STATUS_LABEL_KEYS[status]] || createAiItem.title
  const isSuccess = status === SUCCEEDED && Boolean(lastResult?.pathname)
  const targetHref = isSuccess ? lastResult.pathname : createAiItem.href
  // Clearing the result below re-renders this component (it subscribes to the store) while
  // the click is still being dispatched, i.e. BEFORE the browser follows the link - and an
  // anchor's activation behaviour reads href off the DOM at that moment, so an unpinned
  // href would send a finished run to /ai instead of the generated model. The pin only has
  // to outlive that rerender: it is released a task later (see handleClick), and anything
  // the store has to say meanwhile (a fresh run in flight, a newer result) supersedes it.
  const isPinned = Boolean(pinnedHref) && !STATUS_ICONS[status]
  const href = isPinned ? pinnedHref : targetHref

  // The backend always ships the Create with AI item, but never render a hrefless anchor
  // if `main_menu_items` ever drops it - the glyph would be a dead control.
  if (!href) {
    return null
  }

  function handleClick() {
    // A settled run stays until the user opens it or the next run starts, so acting on
    // it here clears it - the glyph drops back to idle instead of re-announcing a run
    // the user has already seen. Nothing to clear means nothing to pin against.
    if (!lastResult) {
      return
    }

    setPinnedHref(targetHref)
    clearGenerationResult()

    // Released one task later, once the anchor's activation behaviour has read the href -
    // that runs at the end of the click's task, so a microtask would unpin too early. This
    // only matters when the page outlives the click (cmd-click opening a new tab, a Back
    // restore from the bfcache): the glyph is idle again there and must link to the studio,
    // not to the model the user already opened.
    setTimeout(() => setPinnedHref(null), 0)
  }

  return (
    <li className="unified-nav__action unified-nav__action--generation">
      <a
        className="unified-nav__action-link"
        href={href}
        aria-label={label}
        data-track-name={createAiItem.dataTrackName}
        data-track-label="Create with AI"
        data-track-text={STATUS_TRACK_TEXTS[status] || IDLE_TRACK_TEXT}
        onClick={handleClick}
      >
        <Icon
          notSpaced
          color="dark"
          size={ICON_SIZE}
          svgComponent={IconComponent}
          className={classnames(
            'unified-nav__generation-icon',
            iconModifier && `unified-nav__generation-icon--${iconModifier}`
          )}
        />
        {showCount && (
          <span className="unified-nav__generation-count" aria-hidden="true">{activeRunCount}</span>
        )}
      </a>
    </li>
  )
}

GenerationStatusAction.propTypes = {
  createAiItem: shape({
    href: string,
    title: string,
    dataTrackName: string,
  }),
  labels: shape({
    generatingTitle: string,
    readyTitle: string,
    errorTitle: string,
    multipleGenerationsEnabled: bool,
  }),
  currentUserId: string,
}

export default GenerationStatusAction
